import {
	Component,
	inject,
	OnInit,
	Type,
	ViewEncapsulation,
} from '@angular/core';
import {
	Export,
	PluginType,
	DynamicModalWrapperService,
	LoginInfo,
} from '@meta-press/shared-plugins';
import {
	NgbActiveModal,
	NgbModal,
	NgbDropdownModule,
	NgbTooltip,
} from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';

import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { UIAnimations } from '@meta-press/shared-plugins';

import { NotificationService } from '../notification-service/notification.service';
import { NotificationHandle } from '../notification/notification-handle';
import { GameProductionState } from '../model/GameProductionState';
import { LoginService } from '../login-service/login.service';
import {
	SourceMaterialService,
	SourceMaterialState,
} from '../source-material-service/source-material.service';
import { ArticleConceptRequest } from '../model/ArticleConceptRequest';

import {
	EditorGameDefinition,
	EditorGameDefinitionPreset,
} from '../EditorGameDefinition';
import { EditorGameDefinitionService } from '../services/editor-game-definition.service';
import { SettingsService } from '../services/settings-service/settings.service';
import { environment } from '../../../environments/environment';
import { AutomaticContentGeneration } from '../model/AutomaticContentGeneration';
import { BatchGenerationService } from '../services/batch-generation.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
	GenerationUtils,
	GenerationRequest,
} from '../services/generation-utils/generation-utils';
import {
	GameTag,
	GameCategory,
	GAME_TAG_DISPLAY_TEXT,
	GAME_CATEGORY_DISPLAY_TEXT,
	TECHNICAL_TAG_ORDER,
	FEATURE_TAG_ORDER,
	GAME_CATEGORY_ORDER,
	getTagGroups,
	TagGroup,
} from '../model/GameClassification';

// Combined type for both regular games and presets
interface GameOrPreset {
	title: string;
	description: string;
	state: GameProductionState;
	thumbnailClass: string;
	tags: GameTag[];
	categories: GameCategory[];
	editorComponent?: (() => Promise<Type<any>>) | undefined;
	supportAutoGeneration?: boolean;
	supportAutoGenerationWithManualSettings?: boolean;
	desktopSupport?: boolean;
	mobileSupport?: boolean;
	cardBackgroundColor?: string;
	// If this is a preset, contains the preset data
	preset?: {
		parentGame: EditorGameDefinition;
		presetData: EditorGameDefinitionPreset;
	};
}

// This components generates and displays any single-article experience based on the given article concept request

@Component({
	selector: 'app-export-multiple-articls-modal',
	imports: [CommonModule, NgbDropdownModule, FormsModule, NgbTooltip],
	standalone: true,
	templateUrl: './export-multiple-articles-modal.component.html',
	styleUrls: [
		'./export-multiple-articles-modal.component.scss',
		'../../../../../libs/shared/styles/src/styles.scss',
	],

	animations: UIAnimations,
	encapsulation: ViewEncapsulation.ShadowDom,
})
export class ExportMultipleArticlesModalComponent implements OnInit {
	private modalService = inject(NgbModal);
	public articleConceptRequest: ArticleConceptRequest =
		new ArticleConceptRequest();
	protected readonly GameType = PluginType;
	readonly GameProductionState: typeof GameProductionState =
		GameProductionState;

	// If true, the selector is a slave and only returns the selected plugin - not open the editor
	public IsSlave = false;

	activeModal = inject(NgbActiveModal);

	// Filter and search properties
	protected searchText = '';
	protected selectedGrouping: 'none' | 'type' | 'category' = 'none'; // 'none', 'type', 'category' - default to 'type'
	protected filterLogic: 'AND' | 'OR' = 'AND'; // Configure AND/OR logic for tag and category filtering
	protected selectedTags: GameTag[] = []; // technical tags from dropdown
	protected selectedCategories: GameCategory[] = []; // category tags from dropdown
	protected selectedStates: GameProductionState[] = []; // state filtering (OR logic), initialized from SettingsService
	protected availableTags: GameTag[] = [];
	protected availableCategories: GameCategory[] = [];

	// Environment settings
	protected showUnfinishedPlugins = environment.showBetaPlugins;
	protected showInternalPlugins = environment.showInternalPlugins;

	// Display text mappings
	protected readonly gameTagDisplayText = GAME_TAG_DISPLAY_TEXT;
	protected readonly gameCategoryDisplayText = GAME_CATEGORY_DISPLAY_TEXT;
	protected readonly technicalTagOrder = TECHNICAL_TAG_ORDER;
	protected readonly featureTagOrder = FEATURE_TAG_ORDER;
	protected readonly gameCategoryOrder = GAME_CATEGORY_ORDER;

	// Enum references for template
	protected readonly GameTag = GameTag;
	protected readonly GameCategory = GameCategory;
	protected readonly AutomaticContentGeneration = AutomaticContentGeneration;

	// View mode properties
	protected viewMode: 'cards' | 'rows' = 'cards';

	// Notification handles for progress tracking
	private backgroundGenerationProgressHandle: NotificationHandle | null =
		null;

	constructor(
		protected sourceMaterialService: SourceMaterialService,
		private loginService: LoginService,
		private notificationService: NotificationService,
		private dynamicModalWrapperService: DynamicModalWrapperService,
		private editorGameDefinitionService: EditorGameDefinitionService,
		private batchGenerationService: BatchGenerationService,
		private generationUtils: GenerationUtils,
		private settingsService: SettingsService,
		private router: Router,
	) {
		// Initialize selected states from SettingsService based on environment
		this.selectedStates =
			this.settingsService.getAvailableProductionStates();
	}
	async ngOnInit(): Promise<void> {
		const user = this.loginService.GetCurrentUser();

		if (user && this.sourceMaterialService.SelectedArticles.length > 0) {
			// Start material sync silently in the background (no notification)
			// Generation flow will subscribe to materialSyncProgress$ and show notifications there
			this.sourceMaterialService.SourceMaterialState =
				SourceMaterialState.Preparing;

			this.batchGenerationService
				.ensureMaterialSynced(
					user,
					this.sourceMaterialService.SelectedArticles,
				)
				.subscribe({
					next: () => {
						this.sourceMaterialService.SourceMaterialState =
							SourceMaterialState.Ready;
						console.log('[ExportModal] Material sync completed');
					},
					error: (error) => {
						this.sourceMaterialService.SourceMaterialState =
							SourceMaterialState.Error;
						console.error(
							'[ExportModal] Material sync error:',
							error,
						);
					},
				});
		} else {
			// No articles selected or no user - material is ready
			this.sourceMaterialService.SourceMaterialState =
				SourceMaterialState.Ready;
		}
	}

	public CloseModal(): void {
		if (this.activeModal) {
			this.activeModal.close();
		}
	}
	public async SelectPlugin(
		item: GameOrPreset,
		mode: 'editor' | 'demo',
		automaticContentGeneration = AutomaticContentGeneration.DoNothing,
	): Promise<void> {
		// Handle background generation first, regardless of slave mode
		if (
			automaticContentGeneration ===
			AutomaticContentGeneration.GenerateInBackgroundWithDefaultSettings
		) {
			await this.handleBackgroundGeneration(item);
			return;
		}

		if (!this.IsSlave) {
			if (item.editorComponent && mode === 'editor') {
				try {
					// Check if editorComponent is a function that returns a Promise
					if (typeof item.editorComponent === 'function') {
						// Dynamically load the editor component
						const componentType = await item.editorComponent();

						if (componentType) {
							const modalRef = this.modalService.open(
								componentType,
								{
									size: 'xl',
									windowClass: 'dark-modal modal-xxl',
									backdrop: 'static',
									keyboard: false,
								},
							);

							// If this is a preset, pass the gameData as exportData
							if (item.preset) {
								modalRef.componentInstance.exportData = {
									gameData: item.preset.presetData.gameData,
								} as Export;
							}

							// Pass the automaticContentGeneration setting
							if (
								automaticContentGeneration !==
								AutomaticContentGeneration.DoNothing
							) {
								modalRef.componentInstance.automaticContentGeneration =
									automaticContentGeneration;
							}
						} else {
							this.notificationService.ShowToast(
								'Editor-komponentti ei ole saatavilla',
								'danger',
							);
							return;
						}
					} else {
						this.notificationService.ShowToast(
							'Legacy editor components not supported',
							'danger',
						);
					}
				} catch (error) {
					console.error('Error loading editor component:', error);
					this.notificationService.ShowToast(
						'Editorin lataaminen epäonnistui',
						'danger',
					);
				}
			} else if (mode === 'demo') {
				// For demo mode, determine what to show
				let gameDataToShow = null;
				let rendererComponent = null;

				if (item.preset) {
					// For presets, use the preset's gameData and parent's renderer
					gameDataToShow = item.preset.presetData.gameData;
					rendererComponent =
						item.preset.parentGame.rendererComponent;
				} else {
					// For regular games, use gamePreview and own renderer
					const parentGame =
						this.editorGameDefinitionService.editorGameDefinitions.find(
							(g) =>
								g.title === item.title &&
								g.description === item.description,
						);
					if (parentGame) {
						gameDataToShow = parentGame.gamePreview;
						rendererComponent = parentGame.rendererComponent;
					}
				}

				if (!gameDataToShow) {
					this.notificationService.ShowToast(
						'Pelidataa ei löydy',
						'danger',
					);
					return;
				}

				// Validate game data before opening preview
				try {
					const { RendererGameDefinitionService } = await import(
						'@meta-press/shared-plugins'
					);
					const gameDefinitionService =
						new RendererGameDefinitionService();

					const validationResult =
						await gameDefinitionService.validateGameDataForImport(
							gameDataToShow.type,
							gameDataToShow,
						);

					if (!validationResult.isValid) {
						// Show specific validation error(s) in danger notification
						const errorMessage =
							validationResult.errors.length > 0
								? `Validointivirhe: ${validationResult.errors.join(', ')}`
								: 'Sisällössä on validointivirheitä. Esikatselu ei ole mahdollinen.';

						this.notificationService.ShowToast(
							errorMessage,
							'danger',
						);
						return; // Exit early without opening modal
					}
				} catch (error) {
					console.error('Error during game data validation:', error);
					this.notificationService.ShowToast(
						'Pelin tietojen validointi epäonnistui, mutta yritetään jatkaa',
						'warning',
					);
				}

				if (rendererComponent && gameDataToShow) {
					try {
						// Dynamically load the component
						const componentType = await rendererComponent();

						// Get the game definition to check display mode settings
						const gameDefinition = item.preset
							? item.preset.parentGame
							: this.editorGameDefinitionService.editorGameDefinitions.find(
									(g) =>
										g.title === item.title &&
										g.description === item.description,
								);

						// Determine window class based on display mode settings
						let windowClass = 'dark-modal';
						if (gameDefinition) {
							const shouldUseFullscreen =
								gameDefinition.displayMode === 'fullscreen' ||
								(gameDefinition.displayMode === 'both' &&
									gameDefinition.defaultDisplayMode ===
										'fullscreen');

							if (shouldUseFullscreen) {
								windowClass += ' modal-xxl';
							}
						}

						const modalRef = this.modalService.open(componentType, {
							size: 'xl',
							windowClass: windowClass,
						});

						// Set component inputs directly
						modalRef.componentInstance.exportData = {
							gameData: gameDataToShow,
						} as Export;
						modalRef.componentInstance.IsSlave = true;

						// Handle outputs from renderer if needed
						if (modalRef.componentInstance.gameOver) {
							modalRef.componentInstance.gameOver.subscribe(
								(results: unknown) => {
									console.log('Game over:', results);
									modalRef.close();
								},
							);
						}

						if (modalRef.componentInstance.scoreChanged) {
							modalRef.componentInstance.scoreChanged.subscribe(
								(score: unknown) => {
									console.log('Score changed:', score);
								},
							);
						}
					} catch (error) {
						console.error('Error loading component:', error);
						this.notificationService.ShowToast(
							'Komponentin lataaminen epäonnistui',
							'danger',
						);
					}
				} else {
					this.notificationService.ShowToast(
						'Esikatselu ei ole saatavilla',
						'danger',
					);
				}
			} else {
				this.notificationService.ShowToast(
					'Valittu peli ei tue editoria tai esikatselua',
					'danger',
				);
			}
		} else {
			// Return selected plugin
			if (this.activeModal) {
				// For presets, return the parent game type, for regular games return their type
				const typeToReturn = item.preset
					? item.preset.parentGame.type
					: this.editorGameDefinitionService.editorGameDefinitions.find(
							(g) =>
								g.title === item.title &&
								g.description === item.description,
						)?.type;
				this.activeModal.close(typeToReturn);
			}
		}
	}

	// Helper method to get random game icons
	getGameIcon(index: number): string {
		const icons = [
			'bi-puzzle',
			'bi-trophy',

			'bi-lightbulb',

			'bi-rocket',
			'bi-gem',
			'bi-star',
		];
		return icons[index % icons.length];
	}

	// Get status badge icon based on development state
	getStatusBadgeText(state: GameProductionState): string {
		switch (state) {
			case GameProductionState.Production:
				return 'Valmis';
			case GameProductionState.Alpha:
				return 'Alpha';
			case GameProductionState.Beta:
				return 'Testissä';
			default:
				return '<i class="bi bi-pencil-fill"></i>';
		}
	}

	// Get status badge color based on development state (Bootstrap colors)
	getStatusBadgeColor(state: GameProductionState): string {
		switch (state) {
			case GameProductionState.Production:
				return '#28a745'; // Green
			case GameProductionState.Alpha:
				return '#ffc107'; // Yellow
			case GameProductionState.Beta:
				return 'var(--bs-warning)'; // Bootstrap warning yellow
			case GameProductionState.Prototype:
				return 'var(--bs-secondary)'; // Bootstrap secondary gray
			default:
				return 'var(--bs-secondary)'; // Bootstrap secondary gray
		}
	}

	// Get card background color for a game or preset
	getCardBackgroundColor(item: GameOrPreset): string {
		// First check if item has a custom color defined
		if (item.preset?.presetData.cardBackgroundColor) {
			return item.preset.presetData.cardBackgroundColor;
		}

		if (item.preset?.parentGame.cardBackgroundColor) {
			return item.preset.parentGame.cardBackgroundColor;
		}

		// Check if the item itself has a cardBackgroundColor property
		const itemWithColor = item as any;
		if (itemWithColor.cardBackgroundColor) {
			return itemWithColor.cardBackgroundColor;
		}

		// Fallback to old logic based on tags and preset status
		if (item.preset) {
			return 'var(--bs-primary)';
		}

		if (item.tags && item.tags.includes(GameTag.Preset)) {
			return '#198754'; // bg-success green
		}

		if (item.tags && item.tags.includes(GameTag.Container)) {
			return '#6f42c1'; // bg-purple
		}

		return 'var(--bs-primary)'; // Default to project's primary color
	}

	// Filter and search methods
	protected getAvailableTags(): GameTag[] {
		if (this.availableTags.length === 0) {
			// Get content tags from game definitions
			const gameTags =
				this.editorGameDefinitionService.editorGameDefinitions.flatMap(
					(game) => game.tags || [],
				);

			// Get tags from presets
			const presetTags =
				this.editorGameDefinitionService.editorGameDefinitions.flatMap(
					(game) =>
						game.presets?.flatMap(
							(preset) =>
								(preset as EditorGameDefinitionPreset).tags ||
								[],
						) || [],
				);

			this.availableTags = [...new Set([...gameTags, ...presetTags])];
		}
		return this.availableTags;
	}

	protected getAvailableCategories(): GameCategory[] {
		if (this.availableCategories.length === 0) {
			// Get categories from game definitions
			const gameCategories =
				this.editorGameDefinitionService.editorGameDefinitions.flatMap(
					(game) => game.categories || [],
				);

			// Get categories from presets
			const presetCategories =
				this.editorGameDefinitionService.editorGameDefinitions.flatMap(
					(game) =>
						game.presets?.flatMap(
							(preset) =>
								(preset as EditorGameDefinitionPreset)
									.categories || [],
						) || [],
				);

			this.availableCategories = [
				...new Set([...gameCategories, ...presetCategories]),
			];
		}
		return this.availableCategories;
	}

	/**
	 * Get tag groups for dropdown display, filtered by available tags/categories
	 */
	protected getTagGroupsForDropdown(): TagGroup[] {
		const availableTags = this.getAvailableTags();
		const availableCategories = this.getAvailableCategories();

		return getTagGroups()
			.map((group) => ({
				...group,
				items: group.items
					.filter((item) => {
						if (item.type === 'tag') {
							return availableTags.includes(
								item.value as GameTag,
							);
						} else {
							return availableCategories.includes(
								item.value as GameCategory,
							);
						}
					})
					.map((item) => ({
						...item,
						available: this.isFilterAvailable(item),
					})),
			}))
			.filter((group) => group.items.length > 0); // Only show groups that have items
	}

	protected isTagSelected(tag: GameTag): boolean {
		return this.selectedTags.includes(tag);
	}

	protected toggleTag(tag: GameTag): void {
		if (this.isTagSelected(tag)) {
			this.selectedTags = this.selectedTags.filter((t) => t !== tag);
		} else {
			this.selectedTags.push(tag);
		}
	}

	protected isCategorySelected(category: GameCategory): boolean {
		return this.selectedCategories.includes(category);
	}

	protected toggleCategory(category: GameCategory): void {
		if (this.isCategorySelected(category)) {
			this.selectedCategories = this.selectedCategories.filter(
				(c) => c !== category,
			);
		} else {
			this.selectedCategories.push(category);
		}
	}

	/**
	 * Toggle a tag or category from grouped dropdown item
	 */
	protected toggleGroupedItem(item: {
		type: 'tag' | 'category';
		value: GameTag | GameCategory;
	}): void {
		if (item.type === 'tag') {
			this.toggleTag(item.value as GameTag);
		} else {
			this.toggleCategory(item.value as GameCategory);
		}
	}

	/**
	 * Check if a grouped item is selected
	 */
	protected isGroupedItemSelected(item: {
		type: 'tag' | 'category';
		value: GameTag | GameCategory;
	}): boolean {
		if (item.type === 'tag') {
			return this.isTagSelected(item.value as GameTag);
		} else {
			return this.isCategorySelected(item.value as GameCategory);
		}
	}

	/**
	 * Check if selecting a filter would produce any results
	 */
	protected isFilterAvailable(item: {
		type: 'tag' | 'category';
		value: GameTag | GameCategory;
	}): boolean {
		// If already selected, it's always available (so user can deselect)
		if (this.isGroupedItemSelected(item)) {
			return true;
		}

		// Simulate adding this filter to current selection
		const tempTags = [...this.selectedTags];
		const tempCategories = [...this.selectedCategories];

		if (item.type === 'tag') {
			tempTags.push(item.value as GameTag);
		} else {
			tempCategories.push(item.value as GameCategory);
		}

		// Check if any items would match with this additional filter
		return (
			this.getFilteredGamesWithFilters(tempTags, tempCategories).length >
			0
		);
	}

	/**
	 * Helper method to get filtered games with specific filters
	 */
	private getFilteredGamesWithFilters(
		tags: GameTag[],
		categories: GameCategory[],
	): GameOrPreset[] {
		// Get all items
		let items: GameOrPreset[] =
			this.editorGameDefinitionService.editorGameDefinitions
				.filter((settings) => settings.editorComponent)
				.map((game) => ({
					title: game.title,
					description: game.description,
					state: game.state,
					thumbnailClass: game.thumbnailClass,
					tags: game.tags || [],
					categories: game.categories || [],
					editorComponent: game.editorComponent,
					supportAutoGeneration: game.supportAutoGeneration ?? true,
					supportAutoGenerationWithManualSettings:
						game.supportAutoGenerationWithManualSettings ?? true,
					desktopSupport: game.desktopSupport ?? true,
					mobileSupport: game.mobileSupport ?? false,
					cardBackgroundColor: game.cardBackgroundColor,
				}));

		// Add presets
		this.editorGameDefinitionService.editorGameDefinitions.forEach(
			(game) => {
				if (game.presets && game.presets.length > 0) {
					game.presets.forEach((preset) => {
						items.push({
							title: preset.name,
							description: preset.description,
							state: preset.state,
							thumbnailClass: preset.thumbnailClass,
							tags: preset.tags || [],
							categories: preset.categories || [],
							editorComponent: game.editorComponent,
							supportAutoGeneration:
								game.supportAutoGeneration ?? true,
							supportAutoGenerationWithManualSettings:
								game.supportAutoGenerationWithManualSettings ??
								true,
							desktopSupport: game.desktopSupport ?? true,
							mobileSupport: game.mobileSupport ?? false,
							cardBackgroundColor:
								preset.cardBackgroundColor ||
								game.cardBackgroundColor,
							preset: {
								parentGame: game,
								presetData: preset,
							},
						});
					});
				}
			},
		);

		// Apply search filter
		if (this.searchText.trim()) {
			const searchLower = this.searchText.toLowerCase();
			items = items.filter(
				(item) =>
					item.title.toLowerCase().includes(searchLower) ||
					item.description.toLowerCase().includes(searchLower),
			);
		}

		// Apply state filter
		if (this.selectedStates.length > 0) {
			items = items.filter((item) =>
				this.selectedStates.includes(item.state),
			);
		}

		// Apply tag filters
		if (tags.length > 0) {
			items = items.filter((item) => {
				if (!item.tags || item.tags.length === 0) {
					return false;
				}
				if (this.filterLogic === 'AND') {
					return tags.every((tag) => item.tags.includes(tag));
				} else {
					return tags.some((tag) => item.tags.includes(tag));
				}
			});
		}

		// Apply category filters
		if (categories.length > 0) {
			items = items.filter((item) => {
				if (!item.categories || item.categories.length === 0) {
					return false;
				}
				if (this.filterLogic === 'AND') {
					return categories.every((cat) =>
						item.categories.includes(cat),
					);
				} else {
					return categories.some((cat) =>
						item.categories.includes(cat),
					);
				}
			});
		}

		return items;
	}

	protected isStateSelected(state: GameProductionState): boolean {
		return this.selectedStates.includes(state);
	}

	protected toggleState(state: GameProductionState): void {
		if (this.isStateSelected(state)) {
			this.selectedStates = this.selectedStates.filter(
				(s) => s !== state,
			);
		} else {
			this.selectedStates.push(state);
		}
	}

	protected getFilteredGames(): GameOrPreset[] {
		// Include all games that have an editor component
		let items: GameOrPreset[] =
			this.editorGameDefinitionService.editorGameDefinitions
				.filter(
					(settings) =>
						settings.editorComponent &&
						(!settings.isInternal || this.showInternalPlugins),
				)
				.map((game) => ({
					title: game.title,
					description: game.description,
					state: game.state,
					thumbnailClass: game.thumbnailClass,
					tags: game.tags || [],
					categories: game.categories || [],
					editorComponent: game.editorComponent,
					supportAutoGeneration: game.supportAutoGeneration ?? true,
					supportAutoGenerationWithManualSettings:
						game.supportAutoGenerationWithManualSettings ?? true,
					desktopSupport: game.desktopSupport ?? true,
					mobileSupport: game.mobileSupport ?? false,
					cardBackgroundColor: game.cardBackgroundColor,
				}));

		// Add presets as separate items
		this.editorGameDefinitionService.editorGameDefinitions.forEach(
			(game) => {
				if (game.presets && game.presets.length > 0) {
					game.presets.forEach((preset) => {
						items.push({
							title: preset.name,
							description: preset.description,
							state: preset.state,
							thumbnailClass: preset.thumbnailClass,
							tags: preset.tags || [],
							categories: preset.categories || [],
							editorComponent: game.editorComponent,
							supportAutoGeneration:
								game.supportAutoGeneration ?? true,
							supportAutoGenerationWithManualSettings:
								game.supportAutoGenerationWithManualSettings ??
								true,
							desktopSupport: game.desktopSupport ?? true,
							mobileSupport: game.mobileSupport ?? false,
							cardBackgroundColor:
								preset.cardBackgroundColor ||
								game.cardBackgroundColor,
							preset: {
								parentGame: game,
								presetData: preset,
							},
						});
					});
				}
			},
		);

		// Apply search filter
		if (this.searchText.trim()) {
			const searchLower = this.searchText.toLowerCase();
			items = items.filter(
				(item) =>
					item.title.toLowerCase().includes(searchLower) ||
					item.description.toLowerCase().includes(searchLower),
			);
		}

		// Apply state filter (OR logic)
		if (this.selectedStates.length > 0) {
			items = items.filter((item) =>
				this.selectedStates.includes(item.state),
			);
		}

		// Apply tag filters based on filterLogic setting
		if (this.selectedTags.length > 0) {
			items = items.filter((item) => {
				if (!item.tags || item.tags.length === 0) {
					return false;
				}
				if (this.filterLogic === 'AND') {
					// AND logic: item must have ALL selected tags
					return this.selectedTags.every((selectedTag) =>
						item.tags.includes(selectedTag),
					);
				} else {
					// OR logic: item must have ANY of the selected tags
					return this.selectedTags.some((selectedTag) =>
						item.tags.includes(selectedTag),
					);
				}
			});
		}

		// Apply category filters based on filterLogic setting
		if (this.selectedCategories.length > 0) {
			items = items.filter((item) => {
				if (!item.categories || item.categories.length === 0) {
					return false;
				}
				if (this.filterLogic === 'AND') {
					// AND logic: item must have ALL selected categories
					return this.selectedCategories.every((selectedCategory) =>
						item.categories.includes(selectedCategory),
					);
				} else {
					// OR logic: item must have ANY of the selected categories
					return this.selectedCategories.some((selectedCategory) =>
						item.categories.includes(selectedCategory),
					);
				}
			});
		}

		// Sort alphabetically by title when no grouping is selected
		if (this.selectedGrouping === 'none') {
			items.sort((a, b) => a.title.localeCompare(b.title, 'fi'));
		}

		return items;
	}

	protected getGroupedGames(): {
		[key: string]: GameOrPreset[];
	} | null {
		if (this.selectedGrouping === 'none') {
			return null;
		}

		const filteredItems = this.getFilteredGames();
		const groups: { [key: string]: GameOrPreset[] } = {};

		if (this.selectedGrouping === 'type') {
			groups[this.gameTagDisplayText[GameTag.Component]] =
				filteredItems.filter(
					(item) =>
						item.tags && item.tags.includes(GameTag.Component),
				);
			groups[this.gameTagDisplayText[GameTag.Container]] =
				filteredItems.filter(
					(item) =>
						item.tags && item.tags.includes(GameTag.Container),
				);
			groups[this.gameTagDisplayText[GameTag.Preset]] =
				filteredItems.filter(
					(item) => item.tags && item.tags.includes(GameTag.Preset),
				);
			// Add items without these tags to 'Muut'
			const others = filteredItems.filter(
				(item) =>
					!item.tags ||
					(!item.tags.includes(GameTag.Component) &&
						!item.tags.includes(GameTag.Container) &&
						!item.tags.includes(GameTag.Preset)),
			);
			if (others.length > 0) {
				groups['Muut'] = others;
			}
		} else if (this.selectedGrouping === 'category') {
			groups[this.gameCategoryDisplayText[GameCategory.EasyGames]] =
				filteredItems.filter(
					(item) =>
						item.categories &&
						item.categories.includes(GameCategory.EasyGames),
				);
			groups[this.gameCategoryDisplayText[GameCategory.AdaptiveReading]] =
				filteredItems.filter(
					(item) =>
						item.categories &&
						item.categories.includes(GameCategory.AdaptiveReading),
				);
			groups[this.gameCategoryDisplayText[GameCategory.AdvancedGames]] =
				filteredItems.filter(
					(item) =>
						item.categories &&
						item.categories.includes(GameCategory.AdvancedGames),
				);
			// Add items without these categories to 'Muut'
			const others = filteredItems.filter(
				(item) =>
					!item.categories ||
					(!item.categories.includes(GameCategory.EasyGames) &&
						!item.categories.includes(
							GameCategory.AdaptiveReading,
						) &&
						!item.categories.includes(GameCategory.AdvancedGames)),
			);
			if (others.length > 0) {
				groups['Muut'] = others;
			}
		}

		// Remove empty groups
		Object.keys(groups).forEach((key) => {
			if (groups[key].length === 0) {
				delete groups[key];
			}
		});

		return groups;
	}

	protected getSortedGroupedGames(groupedGames: {
		[key: string]: GameOrPreset[];
	}): { key: string; value: GameOrPreset[] }[] {
		const entries = Object.entries(groupedGames).map(([key, value]) => ({
			key,
			value,
		}));

		// Sort by manual order depending on grouping type
		if (this.selectedGrouping === 'type') {
			// Sort by GameTag order (both technical and feature tags)
			const allTags = [
				...this.technicalTagOrder,
				...this.featureTagOrder,
			];
			return entries.sort((a, b) => {
				const aIndex = allTags.findIndex(
					(tag: GameTag) => this.gameTagDisplayText[tag] === a.key,
				);
				const bIndex = allTags.findIndex(
					(tag: GameTag) => this.gameTagDisplayText[tag] === b.key,
				);

				// Put 'Muut' at the end
				if (a.key === 'Muut') return 1;
				if (b.key === 'Muut') return -1;

				// Use manual order
				if (aIndex === -1 && bIndex === -1)
					return a.key.localeCompare(b.key);
				if (aIndex === -1) return 1;
				if (bIndex === -1) return -1;
				return aIndex - bIndex;
			});
		} else if (this.selectedGrouping === 'category') {
			// Sort by GameCategory order
			return entries.sort((a, b) => {
				const aIndex = this.gameCategoryOrder.findIndex(
					(category) =>
						this.gameCategoryDisplayText[category] === a.key,
				);
				const bIndex = this.gameCategoryOrder.findIndex(
					(category) =>
						this.gameCategoryDisplayText[category] === b.key,
				);

				// Put 'Muut' at the end
				if (a.key === 'Muut') return 1;
				if (b.key === 'Muut') return -1;

				// Use manual order
				if (aIndex === -1 && bIndex === -1)
					return a.key.localeCompare(b.key);
				if (aIndex === -1) return 1;
				if (bIndex === -1) return -1;
				return aIndex - bIndex;
			});
		} else {
			// Default: sort by count (fallback)
			return entries.sort((a, b) => b.value.length - a.value.length);
		}
	}

	// View mode methods
	protected toggleViewMode(mode: 'cards' | 'rows'): void {
		this.viewMode = mode;
	}

	/**
	 * Create a background generation request with steps.
	 * Uses background-optimized generation steps from the service.
	 * This mirrors the base editor component pattern for consistency.
	 * @param gameType The plugin type
	 * @param user Current user
	 * @param parameters Component-specific parameters to merge with background defaults
	 * @returns Generation request with steps created from background defaults
	 */
	private createBackgroundGenerationRequest(
		gameType: PluginType,
		user: LoginInfo,
		parameters: any,
	): GenerationRequest {
		// Construct source material
		const hasSourceMaterial =
			this.sourceMaterialService.SelectedArticles.length > 0;
		const sourceMaterial = hasSourceMaterial
			? {
					selectedArticles:
						this.sourceMaterialService.SelectedArticles,
					fileHandles:
						this.sourceMaterialService.GetHandlesForSelectedFiles(
							user,
						),
					imagesAsString: '', // Will be populated by generation utils
				}
			: undefined;

		// Delegate to GenerationUtils for background request creation with steps
		return this.generationUtils.createBackgroundRequest(
			gameType,
			user,
			parameters,
			sourceMaterial,
		);
	}

	/**
	 * Handle background generation with default settings.
	 * Uses background-optimized generation steps automatically via isBackgroundRequest flag.
	 */
	private async handleBackgroundGeneration(
		item: GameOrPreset,
	): Promise<void> {
		// Get the actual game type
		const gameDefinition = item.preset
			? item.preset.parentGame
			: this.editorGameDefinitionService.editorGameDefinitions.find(
					(g) =>
						g.title === item.title &&
						g.description === item.description,
				);

		if (!gameDefinition) {
			this.notificationService.ShowToast(
				'Pelityyppiä ei löytynyt',
				'danger',
			);
			return;
		}

		const user = this.loginService.GetCurrentUser();
		if (!user) {
			this.notificationService.ShowToast(
				'Käyttäjä ei ole kirjautunut',
				'danger',
			);
			return;
		}

		// Track if cancelled during sync phase using Subject for proper observable cancellation
		let isCancelledDuringSync = false;
		const cancelSyncSubject = new Subject<void>();

		// Create notification immediately showing upload/sync state with stop button
		this.backgroundGenerationProgressHandle =
			this.notificationService.createNotification(
				'Tarkistetaan lähdemateriaalin tilaa...',
				{
					style: 'primary',
					timeout: -1,
					showCloseButton: true,
					progress: -2, // Spinner
					actionButton: {
						buttonText: '',
						iconClass: 'bi bi-stop-fill',
						callback: () => {
							isCancelledDuringSync = true;
							// Signal cancellation to the takeUntil operator
							cancelSyncSubject.next();
							cancelSyncSubject.complete();
							this.batchGenerationService.cancelMaterialSync();
							if (this.backgroundGenerationProgressHandle) {
								this.backgroundGenerationProgressHandle.update({
									message:
										'Lähdemateriaalin lähetys keskeytetty',
									style: 'warning',
									progress: -1,
									actionButton: undefined,
									autoClose: true,
									showCloseButton: false,
								});
							}
						},
					},
				},
			);

		// Ensure source material is synced first
		const articles = this.sourceMaterialService.SelectedArticles;
		if (articles.length > 0) {
			try {
				// Use takeUntil with the cancel subject so the observable terminates when cancelled
				await firstValueFrom(
					this.batchGenerationService
						.ensureMaterialSynced(user, articles)
						.pipe(takeUntil(cancelSyncSubject)),
				);
			} catch (syncError) {
				// Check if this was a cancellation (takeUntil causes "no elements in sequence")
				if (isCancelledDuringSync) {
					console.log(
						'[handleBackgroundGeneration] Sync was cancelled by user',
					);
					return; // Silently return - notification already updated
				}
				console.error('Error syncing source material:', syncError);
				if (this.backgroundGenerationProgressHandle) {
					this.backgroundGenerationProgressHandle.update({
						message: 'Lähdemateriaalin synkronointi epäonnistui',
						style: 'danger',
						progress: -1,
						autoClose: true,
						showCloseButton: false,
					});
				}
				return;
			}
		}

		// Double-check if user cancelled during sync phase (in case sync completed just before cancel)
		if (isCancelledDuringSync) {
			return; // Don't proceed with generation
		}

		// Track current progress (starts at 10%, increments by 10% per poll, caps at 95%)
		let currentProgress = 10;

		// Create background generation request
		// BatchGenerationService will automatically use background-specific steps
		// based on the isBackgroundRequest flag
		const request = this.createBackgroundGenerationRequest(
			gameDefinition.type,
			user,
			{}, // Empty parameters - will be populated from background steps
		);

		// Add lifecycle hooks for progress updates during polling
		request.lifecycleHooks = {
			onPollAttempt: (
				taskId: string,
				attempt: number,
				maxAttempts: number,
			) => {
				// Increment progress by 10%, cap at 95%
				currentProgress = Math.min(currentProgress + 10, 95);
				console.log(
					`[onPollAttempt] taskId=${taskId}, attempt=${attempt}/${maxAttempts}, progress=${currentProgress}`,
				);
				if (this.backgroundGenerationProgressHandle) {
					this.backgroundGenerationProgressHandle.update({
						message: `Luodaan peliä "${item.title}"...`,
						progress: currentProgress,
						style: 'primary',
					});
				} else {
					console.log('[onPollAttempt] No notification handle!');
				}
			},
		};

		try {
			// Queue the generation and get the job ID
			const jobId =
				await this.batchGenerationService.queueGeneration(request);

			// Update notification with generation progress and cancel button
			if (this.backgroundGenerationProgressHandle) {
				this.backgroundGenerationProgressHandle.update({
					message: `Luodaan peliä "${item.title}"...`,
					progress: currentProgress,
					actionButton: {
						buttonText: '',
						iconClass: 'bi bi-stop-fill',
						callback: () => {
							// Cancel the job
							this.batchGenerationService
								.cancelBatchJob(jobId)
								.subscribe({
									next: () => {
										if (
											this
												.backgroundGenerationProgressHandle
										) {
											this.backgroundGenerationProgressHandle.update(
												{
													message:
														'Luonti keskeytetty',
													style: 'warning',
													progress: -1,
													actionButton: undefined,
													autoClose: true,
													showCloseButton: true,
												},
											);
										}
									},
									error: (err) => {
										console.error(
											'Error cancelling job:',
											err,
										);
									},
								});
						},
					},
				});
			}

			// Subscribe to completion
			const completionSub =
				this.batchGenerationService.onJobCompleted.subscribe((job) => {
					if (job.id !== jobId) return;

					// Update notification to completed state
					if (this.backgroundGenerationProgressHandle) {
						this.backgroundGenerationProgressHandle.update({
							message: `Peli "${item.title}" valmis!`,
							progress: 100,
							style: 'success',
							actionButton: {
								buttonText: 'Näytä',
								iconClass: '',
								callback: () => {
									this.modalService.dismissAll();
									this.router.navigate(['/exports']);
									this.backgroundGenerationProgressHandle?.close();
								},
							},
						});

						// Don't auto-close notification anymore - let user click the button or close manually
					}

					// Cleanup subscriptions
					completionSub.unsubscribe();
					failureSub.unsubscribe();
				});

			// Subscribe to failures
			const failureSub =
				this.batchGenerationService.onJobFailed.subscribe((job) => {
					if (job.id !== jobId) return;

					// Update notification to failed state
					if (this.backgroundGenerationProgressHandle) {
						this.backgroundGenerationProgressHandle.update({
							message: `"${item.title}" luonti epäonnistui`,
							style: 'danger',
							actionButton: undefined, // Hide cancel button
						});

						// Close notification after a delay
						setTimeout(() => {
							if (this.backgroundGenerationProgressHandle) {
								this.backgroundGenerationProgressHandle.close();
								this.backgroundGenerationProgressHandle = null;
							}
						}, 5000);
					}

					// Cleanup subscriptions
					completionSub.unsubscribe();
					failureSub.unsubscribe();
				});

			// this.notificationService.ShowToast(
			// 	`"${item.title}" luodaan taustalla. Voit tarkistaa tilanteen Tallennetuissa.`,
			// 	'success',
			// );
		} catch (error) {
			console.error('Error queuing background generation:', error);
			this.notificationService.ShowToast(
				'Taustaluonnin käynnistys epäonnistui',
				'danger',
			);
		}
	}
}
